// ProfileHeader.tsx
import React, { useState } from "react";
import {
  ActionIcon,
  Button,
  Flex,
  Group,
  Menu,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconMailPlus,
  IconCheck,
  IconLock,
  IconLockOpen,
  IconDownload,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useNavigate, useParams } from "react-router-dom";
import EmailModal from "@/components/Tax Pro Only/Modals/EmailModal/EmailModal";
import Assignee from "@/components/Tax Pro Only/Assignee/Assignee";
import OrganizerStatus from "@/components/Tax Pro Only/OrganizerStatus/OrganizerStatus";
import ClientStatus from "@/components/Tax Pro Only/ClientStatus/ClientStatus";
import "../styles/styles.scss";
import { useProfileStore } from "../store/profileStore";
import SmartSync from "@/components/SmartSync/SmartSync";
import { useExtensionBannerStore } from "@/utils/stores/useExtensionBannerStore";
import { useExtensionCheck } from "@/utils/hooks/useExtensionCheck";
import InviteClientModal from "./InviteClientModal";
import { getConsistentColorForUser } from "@/pages/TaxProClientList/components/ClientListTable/utils/assigneeUtils";
import { useAuthStore } from "@/utils/stores/authStore";
import { AppRoutes } from "@/constants";
import { useClientExport } from "@/utils/hooks/useClientExport";

const ProfileHeader: React.FC = () => {
  const { user } = useAuthStore();
  const isClient = user?.role === "client";
  const [emailOpened, { open: openEmail, close: closeEmail }] =
    useDisclosure(false);
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const {
    triggerEmailRefresh,
    review,
    setReview,
    headerData,
    reset,
    activeYear,
    availableYears,
    createdFromIntakeFlow,
    invitedToPortal,
    refetch,
  } = useProfileStore();

  const { startExport, isExporting } = useClientExport({
    clientId: isClient ? undefined : (userId ?? undefined),
    availableYears: availableYears ?? [],
  });

  const [inviteModalOpened, setInviteModalOpened] = useState(false);

  const handleEmailSent = (): void => {
    triggerEmailRefresh();
  };

  const handleRedirect = (url: string): void => {
    navigate(url);
  };

  // Construct full name with proper fallback logic
  const firstName = headerData?.basicInfo?.firstName;
  const lastName = headerData?.basicInfo?.lastName;
  const fullName =
    firstName || lastName
      ? `${firstName ?? ""} ${lastName ?? ""}`.trim()
      : "Client";

  const defaultAssignee = { userId: "", firstName: "", lastName: "" };

  // Get consistent color for assignee based on userId
  const assigneeColor = getConsistentColorForUser(headerData?.assignee?.userId);
  const targetUser = {
    email: headerData?.basicInfo?.email ?? "",
    firstName: headerData?.basicInfo?.firstName ?? "",
    lastName: headerData?.basicInfo?.lastName ?? "",
    accountCreated: { date: "", age: 0 },
    feePaid: false,
    phone: "",
    role: "",
    status: headerData?.status?.status ?? "",
    userId: userId ?? "",
    user: fullName !== "Client" ? fullName : "",
    assignee: headerData?.assignee ?? defaultAssignee,
  };

  // Smart Sync
  const { setShouldWiggle } = useExtensionBannerStore();
  const isExtensionInstalled = useExtensionCheck();

  const handleSmartSyncClick = (): void => {
    if (!isExtensionInstalled) {
      setShouldWiggle(true);
    }
  };

  // Add a handler to trigger refetch after invite is sent
  const handleInviteSent = () => {
    setInviteModalOpened(false);
    refetch();
  };

  const renderContent = (): JSX.Element | null => {
    return (
      <>
        <Flex w="100%" justify="space-between" align="center" gap="md">
          <Group
            gap="md"
            className="client-profile-header"
            w={{ base: "100%", md: "auto" }}
            align="center"
            wrap="nowrap"
          >
            <ActionIcon
              variant="transparent"
              color="black"
              onClick={() => {
                reset();
                handleRedirect(
                  isClient ? AppRoutes.taxOrganizerHome : "/clients"
                );
              }}
            >
              <IconArrowLeft size={24} />
            </ActionIcon>
            <Text size="xl" fw={600}>
              {fullName}
            </Text>
            <Tooltip
              label={
                review ? "Lock (Exit Edit Mode)" : "Unlock (Enter Edit Mode)"
              }
              openDelay={500}
            >
              <ActionIcon
                variant="transparent"
                color="black"
                onClick={() => {
                  setReview(!review);
                  // When exiting edit mode (locking), refetch to show latest saved values
                  if (review) {
                    refetch();
                  }
                }}
                title={
                  review ? "Lock (Exit Edit Mode)" : "Unlock (Enter Edit Mode)"
                }
              >
                {review ? <IconLockOpen size={20} /> : <IconLock size={20} />}
              </ActionIcon>
            </Tooltip>
            {!isClient && (
              <>
                <ActionIcon onClick={openEmail} variant="transparent">
                  <Tooltip label="Send Email" openDelay={500}>
                    <IconMailPlus size={22} />
                  </Tooltip>
                </ActionIcon>

                <EmailModal
                  emailOpened={emailOpened}
                  closeEmail={closeEmail}
                  source={"clientProfile"}
                  targetUser={targetUser}
                  onEmailSent={handleEmailSent}
                />
              </>
            )}
            <Menu
              position="bottom"
              shadow="md"
              withArrow
              disabled={isExporting}
            >
              <Menu.Target>
                <Button
                  variant="subtle"
                  size="compact-sm"
                  loading={isExporting}
                >
                  <Tooltip
                    label={isClient ? "Export Your Data" : "Export Client Data"}
                    openDelay={500}
                  >
                    <IconDownload size={22} />
                  </Tooltip>
                </Button>
              </Menu.Target>
              <Menu.Dropdown p="sm">
                <Menu.Item
                  onClick={() => startExport("all")}
                  disabled={isExporting}
                >
                  <Text size="md" ta="center">
                    All Years
                  </Text>
                </Menu.Item>
                {(availableYears ?? []).map((year) => (
                  <Menu.Item
                    key={year}
                    onClick={() => startExport(year)}
                    disabled={isExporting}
                  >
                    <Text size="md" ta="center">
                      {year}
                    </Text>
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          </Group>
          {!isClient && (
            <Group gap="sm">
              {createdFromIntakeFlow && !invitedToPortal && (
                <Button
                  variant="outline"
                  onClick={() => setInviteModalOpened(true)}
                >
                  Invite Client to Portal
                </Button>
              )}
              {createdFromIntakeFlow && invitedToPortal && (
                <Button
                  variant="light"
                  color="green"
                  leftSection={<IconCheck size={18} />}
                  disabled
                  style={{ cursor: "not-allowed" }}
                >
                  Sent
                </Button>
              )}
              <Group data-tooltip-id="smart-sync">
                <SmartSync
                  copyButton={true}
                  onClick={handleSmartSyncClick}
                  name="header-smartsync"
                  newProfile={true}
                  taxYear={activeYear ?? undefined}
                />
              </Group>
            </Group>
          )}
        </Flex>
        <InviteClientModal
          opened={inviteModalOpened}
          onClose={() => setInviteModalOpened(false)}
          clientEmail={headerData?.basicInfo?.email ?? ""}
          clientFirstName={headerData?.basicInfo?.firstName ?? ""}
          clientLastName={headerData?.basicInfo?.lastName ?? ""}
          onInviteSent={handleInviteSent}
        />
        {!isClient && (
          <Flex
            mt="md"
            gap="xl"
            wrap="wrap"
            direction={{ base: "column", md: "row" }}
          >
            <Stack m={10}>
              <Text size="sm" c="dimmed">
                Assignee
              </Text>
              <Assignee
                assignee={
                  headerData?.assignee ?? { firstName: "", lastName: "" }
                }
                clientId={userId ?? ""}
                color={assigneeColor}
              />
            </Stack>
            <Stack m={10}>
              <Text size="sm" c="dimmed">
                Client Status
              </Text>
              <ClientStatus
                status={headerData?.status?.status ?? ""}
                userId={userId ?? ""}
                taxYear={activeYear ?? undefined}
              />
            </Stack>
            <Stack m={10}>
              <Text size="sm" c="dimmed">
                Organizer Status
              </Text>
              <OrganizerStatus
                organizerStatus={headerData?.organizerStatus?.status ?? ""}
                organizerDate={headerData?.organizerStatus?.submitDate}
                resubmitted={
                  headerData?.organizerStatus?.submitDate !==
                  headerData?.organizerStatus?.updateDate
                }
              />
            </Stack>
          </Flex>
        )}
      </>
    );
  };

  return <Paper p="md">{renderContent()}</Paper>;
};

export default ProfileHeader;
